import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Camera, Loader2 } from "lucide-react";
import Breadcrumb from "@/components/site/Breadcrumb";
import SEO from "@/components/site/SEO";
import NotFoundPage from "@/pages/NotFoundPage";
import { api } from "@/lib/api";
import { categoryName } from "@/lib/categories";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const [state, setState] = useState({ loading: true, product: null, notFound: false });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, product: null, notFound: false });
    api.get(`/products/${slug}`)
      .then(({ data }) => { if (!cancelled) setState({ loading: false, product: data, notFound: false }); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setState({ loading: false, product: null, notFound: true });
        else setState({ loading: false, product: null, notFound: false });
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-mist-off">
        <Loader2 className="h-8 w-8 text-mist-navy animate-spin" aria-hidden />
      </div>
    );
  }
  if (state.notFound) return <NotFoundPage />;
  if (!state.product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 bg-mist-off text-mist-ink">
        <AlertCircle className="h-8 w-8 text-mist-navy" aria-hidden />
        <div className="text-lg font-semibold text-mist-navy">Content unavailable</div>
      </div>
    );
  }

  const p = state.product;
  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: "Medical Laboratory", href: "/medical-laboratory" },
    { label: p.name, href: null },
  ];
  const subject = p.inquiry_subject && p.inquiry_subject.trim().length > 0
    ? p.inquiry_subject
    : `Request a Quote for ${p.name}`;
  const contactHref = `/request-a-quote?product=${encodeURIComponent(p.slug)}&subject=${encodeURIComponent(subject)}`;

  const seo = {
    title: (p.seo && p.seo.title) || `${p.name} — Medical Laboratory Equipment | MIST`,
    description: (p.seo && p.seo.description) || "",
  };

  return (
    <>
      <SEO seo={seo} />
      <section className="bg-mist-navy text-white pt-32 pb-10 lg:pt-40 lg:pb-12">
        <div className="container-mist">
          <Breadcrumb items={breadcrumbs} />
        </div>
      </section>
      <section className="bg-white py-16 md:py-24">
        <div className="container-mist grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="bg-mist-off rounded-xl aspect-square flex items-center justify-center overflow-hidden border border-black/[0.05] p-10 md:p-12">
            {p.image ? (
              <picture>
                {p.image_fallback && <source srcSet={`${p.image}?v=analytical-v1`} type="image/webp" />}
                <img
                  src={`${p.image_fallback || p.image}?v=analytical-v1`}
                  alt={p.name}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-contain"
                  data-testid="product-image"
                />
              </picture>
            ) : (
              <div className="flex flex-col items-center gap-2 text-mist-navy/40">
                <Camera className="h-10 w-10" strokeWidth={1.5} aria-hidden />
                <span className="text-sm">Image coming soon</span>
              </div>
            )}
          </div>
          <div className="lg:pt-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-mist-gold font-semibold mb-3">
              {categoryName(p.category)}
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.02em] text-mist-navy">
              {p.name}
            </h1>
            <div className="mt-5 h-[3px] w-10 bg-mist-gold" aria-hidden />
            {p.description && (
              <p className="mt-8 text-[17px] leading-relaxed text-mist-navy/75 max-w-[540px] whitespace-pre-line">
                {p.description}
              </p>
            )}
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link
                to={contactHref}
                data-testid="request-inquiry"
                className="inline-flex items-center gap-2 px-8 h-12 rounded-md bg-mist-gold text-mist-navy text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#b8994f] transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2"
              >
                Request a Quote for this product
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link
                to="/medical-laboratory"
                className="text-sm font-semibold text-mist-navy hover:text-mist-gold transition-colors duration-200 underline underline-offset-4 decoration-mist-navy/20 hover:decoration-mist-gold"
              >
                ← Back to catalog
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
